import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../api";
import { usePermissions } from "../permissions";
import { formatDate } from "../utils";
import { Card, ErrorNotice, Loading } from "./Ui";
import {
  estimateCoverage,
  knownWorkSprints,
  signalStyles,
} from "../delivery-insights";
type Cycle = {
  id: string;
  name: string;
  startsAt: string;
  endsAt: string;
  scope: number | null;
  completed: number | null;
  issueCount: number | null;
  completedCount: number | null;
};
export type DeliverySummary = {
  team: string;
  teamKey: string;
  timezone: string;
  activeCycle: Cycle | null;
  history: Cycle[];
  open: number;
  historicalBacklog: number;
  medianCompletionDays: number | null;
  completionSamples: number;
  currentCount: number;
  completedCount: number;
  completedPercent: number | null;
  completedPointsPercent: number | null;
  outsideCycle: number;
  carryOver: number;
  unknownHistory: number;
  warnings: Record<string, number>;
  committedWarnings?: Record<string, number>;
  projects: {
    id: string;
    name: string;
    open: number;
    completed: number;
    category: string;
    committed: number;
    missing: number;
    statuses: Record<string, number>;
  }[];
  excludedFromCapacity: number;
  workMix: {
    category: string;
    count: number;
    points: number;
    missing: number;
    baseline: number | null;
    sprints: number | null;
  }[];
  baseline: number | null;
  forecast: {
    remainingItems: number;
    estimatedItems: number;
    remainingPoints: number;
    throughput: number | null;
    cycles: number | null;
    reason: string | null;
  };
};
type Item = {
  committed: boolean;
  id: string;
  identifier: string;
  title: string;
  url: string;
  estimate: number | null;
  state: { name: string; type: string };
  assignee: { id: string; name: string } | null;
  project: { id: string; name: string } | null;
  cycle: { id: string; name: string | null; number: number } | null;
  daysInStatus: number | null;
  flags: string[];
  blockers: { identifier: string }[];
  historyComplete: boolean;
  blockersComplete: boolean;
};
export function WorkMix({
  summary,
  stale = false,
}: {
  summary: DeliverySummary;
  stale?: boolean;
}) {
  return (
    <section className="delivery-work-mix">
      <h3>Run, Build and Unplanned work</h3>
      <p className="section-help">
        Eligible unfinished delivery only. Categories are configured by project,
        not inferred. Unclassified work is not silently counted as Build.
      </p>
      <div className="table-wrap">
        <table className="initiative-table">
          <thead>
            <tr>
              <th>Work category</th>
              <th>Items</th>
              <th>Known points</th>
              <th>Missing estimates</th>
              <th>Build forecast</th>
            </tr>
          </thead>
          <tbody>
            {(summary.workMix ?? []).map((w) => (
              <tr key={w.category}>
                <td>
                  {
                    (
                      {
                        run: "Run — recurring operations",
                        build: "Build — planned change",
                        unplanned: "Unplanned work",
                        unclassified: "Unclassified",
                      } as Record<string, string>
                    )[w.category]
                  }
                </td>
                <td>{w.count}</td>
                <td>{w.points}</td>
                <td>{w.missing}</td>
                <td>
                  {w.category !== "build"
                    ? "Not applicable"
                    : stale
                      ? "Stale — sync required"
                      : w.sprints === null
                        ? "Insufficient estimates / history"
                        : `${w.sprints} sprint-equivalents`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="section-help">
        Forecasts use comparable completed cycles with the same assignee
        exclusions and work category. Historical attribution uses current issue
        owners and classifications, not frozen sprint-close assignments. No
        points-to-hours conversion or individual performance ranking.
      </p>
    </section>
  );
}
export function DeliveryOverview({
  sourceId,
  summary: s,
  syncedAt,
}: {
  sourceId: string;
  summary: DeliverySummary;
  syncedAt: string | null;
}) {
  const permissions = usePermissions();
  const [details, setDetails] = useState(false);
  const [scope, setScope] = useState("committed");
  const [detailFilter, setDetailFilter] = useState("open");
  const [drilldownRequest, setDrilldownRequest] = useState(0);
  const stale = !syncedAt || Date.now() - Date.parse(syncedAt) > 1800000;
  const warnings =
    scope === "committed" ? (s.committedWarnings ?? {}) : s.warnings;
  const knownOnly = knownWorkSprints(
    s.forecast?.remainingPoints ?? 0,
    s.forecast?.throughput ?? null,
    Boolean(s.activeCycle),
    stale,
  );
  const openFilter = (filter: string) => {
    setDetailFilter(filter);
    // Every card action is a new request, even if the last requested filter
    // matches. Reset any manual filter, owner and page changes in the child.
    setDrilldownRequest((request) => request + 1);
    setDetails(true);
  };
  return (
    <Card className="span-2 delivery-overview">
      <div className="card-header">
        <div>
          <h2>{s.team} delivery</h2>
          <p className="section-help">
            {s.teamKey} · {s.activeCycle?.name ?? "No active cycle"} ·{" "}
            {s.timezone}
          </p>
        </div>
        <span
          className={`status-badge ${stale ? "status-rejected" : "status-approved"}`}
        >
          {stale ? "Stale snapshot" : "Synced"}
        </span>
      </div>
      <p className="section-help">
        Last imported {formatDate(syncedAt)}.{" "}
        {s.activeCycle
          ? `${formatDate(s.activeCycle.startsAt)} – ${formatDate(s.activeCycle.endsAt)}`
          : "Cycle-based capacity is unknown until a cycle is active."}
      </p>
      <div className="delivery-metrics">
        {[
          [
            "Current-cycle completion",
            s.completedPercent === null ? "Unknown" : `${s.completedPercent}%`,
          ],
          ["Open imported items", s.open],
          ["Outside current cycle", s.outsideCycle],
          ["Carry-over items", s.carryOver],
        ].map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      <p className="section-help">
        Completion: {s.completedCount} of {s.currentCount} current-cycle issues;
        estimated-point completion{" "}
        {s.completedPointsPercent === null
          ? "unknown"
          : `${s.completedPointsPercent}%`}
        . Started work is not counted as completed.
      </p>
      <h3>Needs attention</h3>
      <label className="attention-scope">
        Attention scope{" "}
        <select value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="committed">Capacity-eligible delivery work</option>
          <option value="all">All open imported work (includes backlog)</option>
        </select>
      </label>
      <p className="section-help">
        Red: delivery risks · Amber: flow delays · Teal: data gaps. Counts
        overlap: one issue may have several signals.{" "}
        {scope === "committed"
          ? "This scope matches the sprint forecast."
          : "Backlog is included here, but not in the committed-work forecast."}
      </p>
      <p className="section-help">
        Team delivery time:{" "}
        {s.medianCompletionDays === null
          ? "not enough history"
          : `${s.medianCompletionDays} working days median from started to completed`}{" "}
        ({s.completionSamples} usable completed issues). This measures elapsed
        flow time, not hours worked, individual efficiency, or business
        outcomes.
      </p>
      <p className="section-help">
        {s.historicalBacklog} open items are outside capacity scope, including
        Todo, Backlog and excluded assignees. {s.excludedFromCapacity} open
        items belong to excluded assignees. These remain visible for
        transparency.
      </p>
      <div className="delivery-flags">
        {Object.entries(warnings).length ? (
          Object.entries(warnings).map(([name, count]) => {
            const style = signalStyles[name] ?? {
              tone: "info",
              group: "Signal",
              action: "Review the affected items.",
            };
            const content = (
              <>
                <span>{style.group}</span>
                <b>
                  {name}: {count}
                </b>
                <small>{style.action}</small>
              </>
            );
            return permissions.can("delivery.individual.view") ? (
              <button
                type="button"
                key={name}
                className={`attention-card attention-${style.tone}`}
                aria-label={`View ${count} ${name} issues in ${scope} scope`}
                onClick={() => openFilter(`flag:${scope}:${name}`)}
              >
                {content}
              </button>
            ) : (
              <div
                key={name}
                className={`attention-card attention-${style.tone}`}
              >
                {content}
              </div>
            );
          })
        ) : (
          <p>No attention flags in this snapshot.</p>
        )}
      </div>
      {details && (
        <p className="section-help">
          <a href={`#delivery-details-${sourceId}`}>
            Go to the filtered issue list
          </a>
        </p>
      )}
      <p className="section-help">
        Signals prompt a discussion—not a performance judgement. Days exclude
        weekends, not public holidays.{" "}
        {s.unknownHistory > 0 &&
          `${s.unknownHistory} open items have unknown or incomplete history/blocker information.`}
      </p>
      <section className="forecast-panel" aria-label="Sprint forecast">
        <h3>All eligible work — sprint-equivalent workload</h3>
        <p className="section-help">
          Includes Run, Build, Unplanned and Unclassified work. This is not a
          product finish date; the separate Build forecast below uses Build-only
          history.
        </p>
        <strong>
          {stale || s.forecast?.cycles == null
            ? "Full forecast unavailable"
            : `${s.forecast.cycles} sprint${s.forecast.cycles === 1 ? "" : "s"}`}
        </strong>
        <p>
          {stale
            ? "Refresh the stale import before using this forecast."
            : s.forecast?.reason}
        </p>
        <p>
          <b>
            Estimate coverage:{" "}
            {estimateCoverage(
              s.forecast?.estimatedItems ?? 0,
              s.forecast?.remainingItems ?? 0,
            ) ?? "—"}
            %
          </b>
        </p>
        {s.forecast?.cycles == null && knownOnly !== null && (
          <p className="forecast-partial">
            <b>Known work only: {knownOnly} sprint-equivalents</b>
            <br />
            Excludes{" "}
            {(s.forecast?.remainingItems ?? 0) -
              (s.forecast?.estimatedItems ?? 0)}{" "}
            unestimated eligible items. This is not a forecast for the whole
            workload, nor an estimate for each individual.
          </p>
        )}
        <details className="forecast-help">
          <summary>How to make the full forecast available</summary>
          <ol>
            <li>
              Review the committed items with department leads; remove work from
              the active commitment only when the team agrees it is backlog.
            </li>
            <li>
              Add team-agreed complexity estimates to missing items in Linear.
              Do not use zero or guessed estimates just to clear this warning.
            </li>
            <li>
              Correct statuses and owners, then use Sync now. Completed work in
              comparable historical cycles also needs estimates for the
              throughput baseline.
            </li>
            <li>
              Keep at least three comparable completed cycles with positive
              median throughput. An inactive cycle or stale import also prevents
              a reliable forecast.
            </li>
          </ol>
        </details>
        {permissions.can("delivery.individual.view") &&
          (s.forecast?.remainingItems ?? 0) >
            (s.forecast?.estimatedItems ?? 0) && (
            <button
              className="btn-outline"
              onClick={() => openFilter("flag:committed:Missing estimate")}
            >
              Review unestimated eligible items
            </button>
          )}
        <p className="section-help">
          {s.forecast?.estimatedItems} of {s.forecast?.remainingItems} eligible
          items estimated · {s.forecast?.remainingPoints} known complexity
          points · {s.forecast?.throughput ?? "Unknown"} median points completed
          per comparable sprint.
        </p>
        <p className="section-help">
          Remaining full-task estimates ÷ eligible historical throughput,
          rounded up. Sprint-equivalents from this snapshot, not a promised
          finish date or additional sprints after the current one. Assumes
          stable scope and team availability; excludes historical backlog,
          holidays and unrecorded work. No fixed conversion from points to
          hours.
        </p>
      </section>
      <WorkMix summary={s} stale={stale} />
      <h3>Team focus</h3>
      <div className="table-wrap">
        <table className="initiative-table">
          <thead>
            <tr>
              <th>Linear project</th>
              <th>Open work</th>
              <th>Completed this cycle</th>
              <th>Status breakdown</th>
            </tr>
          </thead>
          <tbody>
            {s.projects.map((p, i) => (
              <tr key={i}>
                <td>{p.name}</td>
                <td>{p.open}</td>
                <td>{p.completed}</td>
                <td>
                  {Object.entries(p.statuses ?? {})
                    .filter(([, n]) => n > 0)
                    .map(([name, n]) => `${name}: ${n}`)
                    .join(" · ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <details>
        <summary>Previous completed cycles ({s.history.length})</summary>
        <p className="section-help">
          Linear’s closing snapshots, not today’s mutable issue list. These
          totals are never added to current workload. Historical points may use
          Linear’s configured default estimates.
        </p>
        <div className="table-wrap">
          <table className="initiative-table">
            <thead>
              <tr>
                <th>Cycle</th>
                <th>Ended</th>
                <th>Completed / scope points</th>
                <th>Completed / total issues</th>
              </tr>
            </thead>
            <tbody>
              {s.history.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{formatDate(c.endsAt)}</td>
                  <td>
                    {c.completed ?? "Unknown"} / {c.scope ?? "Unknown"}
                  </td>
                  <td>
                    {c.completedCount ?? "Unknown"} /{" "}
                    {c.issueCount ?? "Unknown"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      {permissions.can("delivery.individual.view") && (
        <>
          <button className="btn-outline" onClick={() => setDetails((v) => !v)}>
            {details ? "Hide individual details" : "View individual details"}
          </button>
          {details && (
            <DeliveryDetails
              key={`${sourceId}:${drilldownRequest}`}
              initialFilter={detailFilter}
              sourceId={sourceId}
              activeId={s.activeCycle?.id}
            />
          )}
        </>
      )}
    </Card>
  );
}
function DeliveryDetails({
  sourceId,
  activeId,
  initialFilter,
}: {
  sourceId: string;
  activeId?: string;
  initialFilter: string;
}) {
  const api = useApi(),
    permissions = usePermissions();
  const [owner, setOwner] = useState(""),
    [filter, setFilter] = useState(initialFilter),
    [page, setPage] = useState(0);
  const query = useQuery({
    queryKey: ["delivery-details", sourceId],
    queryFn: () =>
      api.get<{ items: Item[]; syncedAt: string; stale: boolean }>(
        `/capacity/sources/${sourceId}/details`,
      ),
    refetchInterval: 60000,
    gcTime: 0,
    retry: false,
  });
  const download = useMutation({
    mutationFn: () =>
      api.download(
        `/capacity/sources/${sourceId}/details?format=csv`,
        "delivery-details.csv",
      ),
  });
  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorNotice error={query.error} />;
  const closed = (i: Item) =>
    ["completed", "canceled", "duplicate"].includes(i.state.type);
  const owners = [
    ...new Map(
      query.data.items
        .filter((i) => i.assignee)
        .map((i) => [i.assignee!.id, i.assignee!.name]),
    ).entries(),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const visible = query.data.items.filter(
    (i) =>
      (!owner || (i.assignee?.id ?? "unassigned") === owner) &&
      (filter === "all" ||
        (filter === "open" && !closed(i)) ||
        (filter === "attention" && i.flags.length) ||
        (filter === "current" && i.cycle?.id === activeId) ||
        (filter === "committed" && i.committed) ||
        (filter.startsWith("flag:") &&
          !closed(i) &&
          (filter.split(":")[1] !== "committed" || i.committed) &&
          i.flags.includes(filter.split(":").slice(2).join(":")))),
  );
  const start =
    Math.min(page, Math.max(0, Math.ceil(visible.length / 20) - 1)) * 20;
  return (
    <section className="delivery-details" id={`delivery-details-${sourceId}`}>
      <h3>Individual workload & tasks</h3>
      <p className="section-help">
        Restricted information. Assignment totals are not individual capacity or
        efficiency scores. They exclude leave, support work and other tools.
        Historical issue details are current records, not frozen end-of-cycle
        assignments.
      </p>
      {query.data.stale && (
        <p role="alert">
          This snapshot is stale. Sync before making allocation decisions.
        </p>
      )}
      <div className="delivery-toolbar">
        <label>
          Owner
          <select
            value={owner}
            onChange={(e) => {
              setOwner(e.target.value);
              setPage(0);
            }}
          >
            <option value="">All owners</option>
            <option value="unassigned">Unassigned</option>
            {owners.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Show
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(0);
            }}
          >
            <option value="open">Open work</option>
            <option value="attention">Needs attention</option>
            <option value="current">Current cycle</option>
            <option value="committed">Capacity-eligible delivery work</option>
            {Object.keys(signalStyles).flatMap((name) =>
              ["committed", "all"].map((scope) => (
                <option
                  key={`${scope}:${name}`}
                  value={`flag:${scope}:${name}`}
                >
                  {name} — {scope === "committed" ? "committed" : "all open"}
                </option>
              )),
            )}
            <option value="all">All imported issues</option>
          </select>
        </label>
        {permissions.can("delivery.individual.export") && (
          <button
            className="btn-outline"
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            {download.isPending ? "Exporting…" : "Export all imported details"}
          </button>
        )}
      </div>
      {download.isError && <ErrorNotice error={download.error} />}
      <p className="section-help">
        Owner totals below cover all open imported work, including backlog; the
        issue list beneath uses your selected filters.
      </p>
      <div className="table-wrap">
        <table className="initiative-table">
          <thead>
            <tr>
              <th>Owner</th>
              <th>Open items</th>
              <th>Known points</th>
              <th>Missing estimates</th>
              <th>Attention items</th>
            </tr>
          </thead>
          <tbody>
            {[...owners, ["unassigned", "Unassigned"]]
              .filter(([id]) => !owner || owner === id)
              .map(([id, name]) => {
                const items = query.data.items.filter(
                  (i) => !closed(i) && (i.assignee?.id ?? "unassigned") === id,
                );
                return (
                  <tr key={id}>
                    <td>{name}</td>
                    <td>{items.length}</td>
                    <td>{items.reduce((n, i) => n + (i.estimate ?? 0), 0)}</td>
                    <td>{items.filter((i) => i.estimate === null).length}</td>
                    <td>{items.filter((i) => i.flags.length).length}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <p>{visible.length} matching issues</p>
      <div className="table-wrap">
        <table className="initiative-table">
          <thead>
            <tr>
              <th>Issue / project</th>
              <th>Owner</th>
              <th>Status / cycle</th>
              <th>Points</th>
              <th>Days in status</th>
              <th>Needs attention</th>
            </tr>
          </thead>
          <tbody>
            {visible.slice(start, start + 20).map((i) => (
              <tr key={i.id}>
                <td>
                  <a href={i.url} target="_blank" rel="noreferrer">
                    {i.identifier}: {i.title}
                  </a>
                  <p className="muted">{i.project?.name ?? "No project"}</p>
                </td>
                <td>{i.assignee?.name ?? "Unassigned"}</td>
                <td>
                  {i.state.name}
                  <p className="muted">
                    {i.cycle?.name ??
                      (i.cycle ? `Cycle ${i.cycle.number}` : "No cycle")}
                  </p>
                </td>
                <td>{i.estimate ?? "Unknown"}</td>
                <td>{closed(i) ? "—" : (i.daysInStatus ?? "Unknown")}</td>
                <td>
                  {i.flags.join(" · ") || "—"}
                  {i.blockers.length > 0 && (
                    <p>
                      Blocked by{" "}
                      {i.blockers.map((b) => b.identifier).join(", ")}
                    </p>
                  )}
                  {(!i.historyComplete || !i.blockersComplete) && (
                    <p className="muted">Incomplete history or dependencies</p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!visible.length && <p>No issues match these filters.</p>}
      <div className="delivery-toolbar">
        <button
          className="btn-outline"
          disabled={start === 0}
          onClick={() => setPage(Math.max(0, page - 1))}
        >
          Previous
        </button>
        <span>
          {visible.length
            ? `${start + 1}–${Math.min(start + 20, visible.length)} of ${visible.length}`
            : "0 results"}
        </span>
        <button
          className="btn-outline"
          disabled={start + 20 >= visible.length}
          onClick={() => setPage(page + 1)}
        >
          Next
        </button>
      </div>
    </section>
  );
}
